/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import { SVGIcon } from '@superops/super-icons';
import { ContractType } from '@nile/types';
import {
  SuperList,
  DisabledTag,
  getTranslatedText,
  DateTimeFormatter,
  ALL_MODULES,
  CurrencyText,
  getDefaultValue,
  useSuperListHook,
  ALL_TABLES,
  getDateOperators
} from '@nile/components';
import {
  TextStyle,
  Link,
  DisplayText,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { useHistory } from 'react-router-dom';
import {
  AccountClient,
  GET_RETAINER_CONSUMPTION_LIST,
  GET_SERVICE_CATALOG_ITEMS
} from '@nile/graphql';
import { AccessControl } from '@nile/components';

const columns = [
  {
    Header: 'common.serviceItems',
    accessor: 'serviceCatalogItem.name'
  },
  {
    Header: 'common.description',
    accessor: 'notes'
  },
  {
    Header: 'common.type',
    accessor: 'serviceCatalogItem.serviceTypeItem.offeringType'
  },
  {
    Header: 'metric.quantity.unitsOrHours',
    accessor: 'revisedQuantity'
  },
  {
    Header: 'common.startDateTime',
    accessor: 'billStartDateTime'
  },
  {
    Header: 'common.endDateTime',
    accessor: 'billDateTime'
  },
  {
    Header: 'msp.contracts.form.field.included.items.total.price',
    accessor: 'revisedAmount'
  }
];

function getSortBy(column: any) {
  switch (column.accessor) {
    case 'revisedQuantity':
    case 'revisedAmount':
      return true;
    default:
      return false;
  }
}

const getFilterType = (column: any) => {
  switch (column.accessor) {
    case 'serviceCatalogItem.name':
    case 'serviceCatalogItem.serviceTypeItem.offeringType':
      return 'select';
    default:
      return 'text';
  }
};

const getDataType = (column: any) => {
  switch (column.accessor) {
    case 'revisedQuantity':
    case 'revisedAmount':
      return 'number';
    case 'billStartDateTime':
    case 'billDateTime':
      return 'daterange';
    default:
      return 'string';
  }
};

const generateOptions = (col: any, clientData: any) => {
  switch (col?.accessor) {
    case 'serviceCatalogItem.name':
      return clientData?.getServiceCatalogItems?.items?.map((el: any) => {
        return {
          ...el,
          name: el.itemId,
          value: el.name,
          label: el.name
        };
      });
    case 'serviceCatalogItem.serviceTypeItem.offeringType':
      return clientData;
    default:
      return null;
  }
};

const checkDynamicQuery = (col: any) => {
  switch (col?.accessor) {
    case 'serviceCatalogItem.name':
    case 'serviceCatalogItem.serviceTypeItem.offeringType':
      return true;
    default:
      return false;
  }
};

function formatOperands(operands: any) {
  return operands?.map((operand: any) => {
    if (
      operand?.attribute === 'serviceCatalogItem.serviceTypeItem.offeringType'
    ) {
      const conditionalOperands = [];
      if (operand?.value.includes('SERVICE')) {
        conditionalOperands.push({
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'serviceCatalogItem.itemType',
              operator: 'is',
              value: 'SERVICE'
            },
            {
              attribute: 'serviceCatalogItem.serviceTypeItem.offeringType',
              operator: 'includes',
              value: ['HOURS', 'QUANTITY']
            }
          ]
        });
      }
      if (operand?.value.includes('PRODUCT')) {
        conditionalOperands.push({
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'serviceCatalogItem.itemType',
              operator: 'is',
              value: 'PRODUCT'
            }
          ]
        });
      }
      return conditionalOperands.length > 1
        ? {
            joinOperator: 'OR',
            operands: conditionalOperands
          }
        : conditionalOperands[0];
    }
    return operand;
  });
}

const DetailedConsumptionReportTable = ({
  clientId,
  contract,
  reportId,
  sharedReport,
  allowedDateRange
}: {
  clientId: string;
  contract?: {
    contractId: string | null;
    contractName?: string;
    contractType?: string | null;
  };
  reportId: string;
  sharedReport: boolean;
  allowedDateRange?: { start: string; end: string };
}) => {
  const intl = useIntl();

  const history = useHistory();

  const [contractType, setContractType] = useState<ContractType | null>(null);

  const [view, setView] = useState<'TICKET' | 'PROJECT'>('TICKET');

  const {
    tableCondition,
    updateTableFilterCondition,
    resetFilters,
    showResetFilter
  } = useSuperListHook({
    tableName: ALL_TABLES.detailedConsumptionReportTable,
    viewId: `${clientId}-${contract?.contractId}-${view}`
  });

  const [data, setData] = useState([]);

  const [columnGroup, setColumnGroup] = useState<any[]>([]);

  function getFilterBy(column: any) {
    switch (column.accessor) {
      case 'revisedQuantity':
      case 'revisedAmount':
        return true;
      case 'billDateTime':
      case 'billStartDateTime':
        return !!sharedReport;
      default:
        return false;
    }
  }

  const getDynamicQuery = (col: any) => {
    switch (col?.accessor) {
      case 'serviceCatalogItem.name':
        return AccountClient.query({
          query: GET_SERVICE_CATALOG_ITEMS,
          variables: {
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
        });
      case 'serviceCatalogItem.serviceTypeItem.offeringType':
        return {
          data: [
            {
              label: getTranslatedText(intl, 'common.service'),
              value: 'SERVICE'
            },
            {
              label: getTranslatedText(
                intl,
                'admin.product.and.services.items.type.product'
              ),
              value: 'PRODUCT'
            }
          ]
        };
      default:
        return null;
    }
  };

  const offeringTypeMap = {
    HOURS: 'common.service',
    QUANTITY: 'common.service',
    PRODUCT: 'admin.product.and.services.items.type.product',
    SERVICE: 'common.service'
  };

  const DynamicCell = ({ cell }: { cell: any }) => {
    const { actions, editable } = cell?.column;
    const rowData = cell?.row?.original;
    const isEditable = editable && rowData?.status !== 'INACTIVE';

    switch (cell.column.accessor) {
      case 'billDateTime':
      case 'billStartDateTime':
        return cell?.value ? (
          <DateTimeFormatter date={cell?.value} type="dateTime" />
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
      case 'revisedAmount':
        return null;
      case 'revisedQuantity':
        return (
          <div className="fw">
            {`${cell?.value ?? '0'} ${
              rowData?.serviceCatalogItem?.serviceTypeItem?.offeringType ===
              'HOURS'
                ? ' hr'
                : ''
            }`}
          </div>
        );
      case 'notes':
        return cell?.value ? (
          <div className="fw line-break-anywhere ellipsis" title={cell?.value}>
            {cell?.value}
          </div>
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
      case 'serviceCatalogItem.serviceTypeItem.offeringType':
        return (
          <div className="fw">
            <FormattedMessage
              id={
                offeringTypeMap[
                  rowData?.serviceCatalogItem
                    ?.itemType as keyof typeof offeringTypeMap
                ]
              }
              defaultMessage={rowData?.serviceCatalogItem?.itemType || ''}
            />
          </div>
        );
      default:
        return cell?.value ? (
          <div>{cell?.value}</div>
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
    }
  };

  const defaultColumnCells = {
    DynamicCell: DynamicCell
  };

  function generateColumns() {
    const columnsGrp = columns.map((col) => {
      return {
        name: col.accessor,
        Header: getTranslatedText(intl, col.Header),
        header: getTranslatedText(intl, col.Header),
        accessor: col.accessor,
        accessorKey: col.accessor,
        id: col.accessor,
        size: 100,
        disableResizing: false,
        disableSortBy: getSortBy(col),
        editable: false,
        disableFilters: getFilterBy(col),
        filterType: getFilterType(col),
        dataType: getDataType(col),
        type: 'DynamicCell',
        actions: null,
        field: {
          name: col.accessor,
          accessor: col.accessor,
          label: getTranslatedText(intl, col.Header),
          sortable: false,
          id: col.accessor,
          dataType: getDataType(col),
          filterType: getFilterType(col)
        },
        helpers: {
          getDefaultValue,
          updateConditions: updateTableFilterCondition,
          checkDynamicQuery,
          getDynamicQuery,
          generateOptions,
          getDateOperators
        }
      };
    });
    setColumnGroup(columnsGrp);
  }

  const defaultCondition = useMemo(() => {
    const condition: any = {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'client.accountId',
          operator: 'is',
          value: clientId
        },
        {
          attribute: 'contract.contractStatus',
          operator: 'is',
          value: 'ACTIVE'
        }
      ]
    };
    if (contract?.contractId) {
      condition.operands.push({
        attribute: 'contract.contractId',
        operator: 'is',
        value: contract?.contractId
      });
    }
    if (view !== null) {
      condition.operands.push({
        attribute: 'workItem.module',
        operator: 'is',
        value: view === 'PROJECT' ? 'PROJECT' : 'TICKET'
      });
    }
    // if (allowedDateRange?.start) {
    //   condition.operands.push({
    //     attribute: 'billStartDateTime',
    //     operator: 'greaterThanOrEqual',
    //     value: allowedDateRange.start
    //   });
    // }
    if (allowedDateRange?.start && allowedDateRange?.end) {
      condition.operands.push({
        attribute: 'billDateTime',
        operator: 'between',
        value: [allowedDateRange.start, allowedDateRange.end]
      });
    }
    return condition;
  }, [clientId, contract?.contractId, view, allowedDateRange]);

  useEffect(() => {
    generateColumns();
  }, []);

  useEffect(() => {
    if (contract?.contractType && contract?.contractType !== null) {
      setContractType(contract?.contractType as ContractType);
      setView(contract?.contractType === 'ONE_TIME' ? 'PROJECT' : 'TICKET');
    } else {
      setContractType(null);
      setView('TICKET');
    }
  }, [contract?.contractType]);

  const AvailableTabs = [
    {
      id: 'TICKET',
      name: 'ticket',
      icon: 'TicketIdentifier',
      contractType: ['SERVICE', 'USAGE']
    },
    {
      id: 'PROJECT',
      name: 'project',
      icon: 'ProjectIdentifier',
      contractType: ['ONE_TIME']
    }
  ];

  return (
    <div className="full-width full-height">
      <div className="position-sticky z400 white-to-grey-bg">
        <div className="fx actr jctr">
          <div className="grid fw actr mb10 p20">
            <DisplayText size="extraLarge" weight="SemiBold">
              <FormattedMessage
                id="invoice.detailedConsumptionReport.table.title"
                defaultMessage="Detailed Consumption Breakdown"
              />
            </DisplayText>
            <TextStyle variation="subdued">
              <FormattedMessage
                id="invoice.detailedConsumptionReport.table.description"
                defaultMessage="Overview of all active retainers and their current utilization status"
              />
            </TextStyle>
          </div>
          <div className="mlauto w100p">
            {showResetFilter && (
              <Button id="resetSuperListFilter" plain onClick={resetFilters}>
                <FormattedMessage id="ticket.details.resetFilters" />
              </Button>
            )}
          </div>
        </div>
        <div className="ml-10 pb10">
          <ButtonGroup segmented>
            {!contractType
              ? AvailableTabs?.map((tab: any) => {
                  return (
                    <Button
                      primary={view === tab.id}
                      selected={view === tab.id}
                      onClick={() => setView(tab.id)}
                      icon={<SVGIcon name={tab.icon} size="sm" />}
                    >
                      <FormattedMessage
                        id={`app.modules.workspace.${tab.name}`}
                      />
                    </Button>
                  );
                })
              : null}
          </ButtonGroup>
        </div>
      </div>
      <div
        className={`min-height-300 ${data?.length === 0 ? 'height300' : ''}`}
      >
        <SuperList
          padded="lg"
          noOverscan
          data={data}
          headerSticky
          stickyTop={contractType ? 94 : 142}
          noBulkSelect
          viewId={`${clientId}-${contract?.contractId}-${view}`}
          setData={setData}
          columns={columnGroup}
          rowCount={data?.length}
          defaultColumnCells={defaultColumnCells}
          additionalVariables={
            sharedReport && reportId ? { sharedReportId: reportId } : {}
          }
          tableCondition={tableCondition}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
            heading: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.heading'
            ),
            description: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.description'
            )
          }}
          groupAccessor={(rowItem: any) => ({
            id: rowItem?.workItem?.workId,
            name: rowItem?.workItem?.subject ? (
              <div className="fx center g10">
                <SVGIcon
                  name={
                    rowItem?.workItem?.module === ALL_MODULES.ticket
                      ? 'TicketIdentifier'
                      : 'ProjectIdentifier'
                  }
                />
                <span className="cw-20">
                  <AccessControl
                    roleToCheck={
                      rowItem?.workItem?.module === ALL_MODULES.ticket
                        ? 'tickets.view'
                        : 'project.view'
                    }
                    render={
                      <Link
                        onClick={() => {
                          history.push(
                            rowItem?.workItem?.module === ALL_MODULES.ticket
                              ? `/tickets/${rowItem?.workItem?.workId}/ticket`
                              : `/project/${rowItem?.workItem?.workId}/detail`
                          );
                        }}
                      >
                        {rowItem?.workItem?.subject}
                      </Link>
                    }
                  />
                </span>
              </div>
            ) : null,
            additionalHeaderProperties: [
              {
                component: ({ headerData }: { headerData: any }) => {
                  const totalPrice = headerData?.subRows?.reduce(
                    (acc: number, sr: any) => acc + Number(sr?.revisedAmount),
                    0
                  );
                  return (
                    <div className="mlauto mr80">
                      <CurrencyText value={totalPrice} />
                    </div>
                  );
                }
              }
            ],
            empty: getTranslatedText(intl, 'app.noData.message')
          })}
          isGroupingEnabled
          defaultExpanded
          idKey="itemId"
          tableName={ALL_TABLES.detailedConsumptionReportTable}
          client={AccountClient}
          query={GET_RETAINER_CONSUMPTION_LIST}
          accessorKey="getRetainerConsumptionList"
          dataKey="items"
          defaultSort={[
            {
              attribute: 'billDateTime',
              order: 'DESC'
            }
          ]}
          defaultCondition={defaultCondition}
          appendVariablesToInput
          formatOperands={formatOperands}
        />
      </div>
    </div>
  );
};

export default DetailedConsumptionReportTable;
